/**
 * React-Toastify relies heavily on its imported CSS.
 * However, our different environments have different methods for handling & bundling imported CSS
 *
 * Our doc site, for example, crashes if we try to import a css file from node_modules.
 *
 * In order to circumvent this, we are manually re-creating the react-toastify CSS and injecting it to root via our emotionStyled instance.
 *
 * @SPRING_CLEANING DO NOT UPGRADE REACT-TOASTIFY WITHOUT UPDATING THIS ROOT CSS.
 */
import {
  animations,
  emotionStyled,
  injectGlobal,
  keyframes,
  coreColorTokens,
} from '@packages/utilities/styled';
import {tokens} from '../../themes';
import {selectors, zIndex} from '../../utils';

import {Button} from '../Button';
import {CloseButton} from '../CloseButton';
import {Note, NoteBody, restyleNoteVars} from '../Note';
import {TOAST_CONTENT_MAX_WIDTH, TOAST_MAX_WIDTH} from './_consts';

const cssVars = {
  '--toastify-color-light': '#fff',
  '--toastify-color-dark': '#121212',
  '--toastify-color-info': '#3498db',
  '--toastify-color-success': '#07bc0c',
  '--toastify-color-warning': '#f1c40f',
  '--toastify-color-error': '#e74c3c',
  '--toastify-color-transparent': 'rgba(255, 255, 255, 0.7)',
  '--toastify-toast-width': '320px',
  '--toastify-toast-background': '#fff',
  '--toastify-toast-min-height': '64px',
  '--toastify-toast-max-height': '800px',
  '--toastify-font-family': 'sans-serif',
  '--toastify-z-index': '9999',
  '--toastify-text-color-light': '#757575',
  '--toastify-text-color-dark': '#fff',
  '--toastify-text-color-info': '#fff',
  '--toastify-text-color-success': '#fff',
  '--toastify-text-color-warning': '#fff',
  '--toastify-text-color-error': '#fff',
  '--toastify-spinner-color': '#616161',
  '--toastify-spinner-color-empty-area': '#e0e0e0',
} as const;

const classDeclarations = {
  '.Toastify__toast-container': {
    transform: 'translate3d(0, 0, var(--toastify-z-index) px)',
    position: 'fixed',
    padding: '4px',
    minWidth: 'var(--toastify-toast-width)',
    width: 'fit-content',
    maxWidth: TOAST_MAX_WIDTH,
    boxSizing: 'border-box',
    zIndex: zIndex.modalNestedPortal,
  },
  '.Toastify__toast-container--top-left': {
    top: 'var(--spring-toast-top, 3em)',
    left: '1em',
  },
  '.Toastify__toast-container--top-center': {
    top: 'var(--spring-toast-top, 3em)',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  '.Toastify__toast-container--top-right': {
    top: 'var(--spring-toast-top, 3em)',
    right: 'var(--spring-toast-right, 1em)',
  },
  '.Toastify__toast-container--bottom-left': {
    bottom: '1em',
    left: '1em',
  },
  '.Toastify__toast-container--bottom-center': {
    bottom: '1em',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  '.Toastify__toast-container--bottom-right': {
    bottom: '1em',
    right: '1em',
  },
  '.Toastify__toast': {
    backgroundColor: 'unset',
    borderRadius: 3,
    minHeight: 'auto',
    overflow: 'hidden',
    boxShadow: tokens.boxShadows.menu,
    padding: 'unset',
    fontFamily: 'inherit',
    width: '100%',
    marginBottom: '1rem',
    '&.toast-draggable': {
      cursor: tokens.ui.activeCursor,
    },
  },
  '.Toastify--animate': {
    animationFillMode: 'both',
    animationDuration: animations.durationSlower,
    /**
     * This is copy-pasted from __experimentalAtoms.reducedMotionAtom.
     * Style Atoms do not work in injectGlobal, as they're designed to work within our custom emotionStyled control flow
     */
    '@media (prefers-reduced-motion)': {
      animationDelay: `0ms !important`,
      animationDuration: `${animations.durationDisabled} !important`,
      transitionDelay: `0ms !important`,
      transitionDuration: `${animations.durationDisabled} !important`,
    },
  },
  '.Toastify__toast--rtl': {
    direction: 'rtl',
  },
  [tokens.breakpoints.bp1Only]: {
    '.Toastify__toast-container': {
      width: '100vw',
      padding: 0,
      left: 0,
      margin: 0,
    },
    '.Toastify__toast-container--top-left, .Toastify__toast-container--top-center, .Toastify__toast-container--top-right':
      {
        // Wins over the base `top: var(--spring-toast-top, 3em)` rule via
        // source order (media queries add no specificity). This is what pins
        // mobile toasts to the top of the viewport; host-supplied inline
        // `--spring-toast-top` values are irrelevant to this element on
        // mobile because we set `top: 0` directly.
        top: 0,
        transform: 'translateX(0)',
      },
    '.Toastify__toast-container--bottom-left, .Toastify__toast-container--bottom-center, .Toastify__toast-container--bottom-right':
      {
        bottom: 0,
        transform: 'translateX(0)',
      },
    '.Toastify__toast-container--rtl': {
      right: 0,
      left: 'initial',
    },
    '.Toastify__toast': {
      marginBottom: 0,
    },
  },
} as const;

/** Animation Keyframes */
const Toastify__slideInRight = keyframes({
  from: {
    transform: 'translate3d(110%, 0, 0)',
    visibility: 'visible',
  },
  to: {
    transform: 'translate3d(0, 0, 0)',
  },
});

const Toastify__slideInLeft = keyframes({
  from: {
    transform: 'translate3d(-110%, 0, 0)',
    visibility: 'visible',
  },
  to: {
    transform: 'translate3d(0, 0, 0)',
  },
});

const Toastify__slideInUp = keyframes({
  from: {
    transform: 'translate3d(0, 110%, 0)',
    visibility: 'visible',
  },
  to: {
    transform: 'translate3d(0, 0, 0)',
  },
});

const Toastify__slideInDown = keyframes({
  from: {
    transform: 'translate3d(0, -110%, 0)',
    visibility: 'visible',
  },
  to: {
    transform: 'translate3d(0, 0, 0)',
  },
});

const Toastify__slideOutRight = keyframes({
  from: {
    transform: 'translate3d(0, 0, 0)',
  },
  to: {
    visibility: 'hidden',
    transform: 'translate3d(110%, 0, 0)',
  },
});

const Toastify__slideOutLeft = keyframes({
  from: {
    transform: 'translate3d(0, 0, 0)',
  },
  to: {
    visibility: 'hidden',
    transform: 'translate3d(-110%, 0, 0)',
  },
});

const Toastify__slideOutDown = keyframes({
  from: {
    transform: 'translate3d(0, 0, 0)',
  },
  to: {
    visibility: 'hidden',
    transform: 'translate3d(0, 500px, 0)',
  },
});

const Toastify__slideOutUp = keyframes({
  from: {
    transform: 'translate3d(0, 0, 0)',
  },
  to: {
    visibility: 'hidden',
    transform: 'translate3d(0, -500px, 0)',
  },
});

// DO NOT REMOVE trackProgress STYLES OR AUTOCLOSE WILL NOT WORK. Even though we're not showing React-toastify's native
// progress bar, we still need these (and other progressbar) styles because the close functionality seems to be
// triggered end of the progressbar animation, even when it is hidden.
// @see https://github.com/fkhadra/react-toastify/issues/223 for some more details.
const Toastify__trackProgress = keyframes({
  from: {
    transform: 'scaleX(1)',
  },
  to: {
    transform: 'scaleX(0)',
  },
});

/** Animation Classes */
const animationClasses = {
  '.Toastify__slide-enter--top-left, .Toastify__slide-enter--bottom-left': {
    animationName: Toastify__slideInLeft,
  },
  '.Toastify__slide-enter--top-right, .Toastify__slide-enter--bottom-right': {
    animationName: Toastify__slideInRight,
  },
  '.Toastify__slide-enter--top-center': {
    animationName: Toastify__slideInDown,
  },
  '.Toastify__slide-enter--bottom-center': {
    animationName: Toastify__slideInUp,
  },

  '.Toastify__slide-exit--top-left, .Toastify__slide-exit--bottom-left': {
    animationName: Toastify__slideOutLeft,
  },
  '.Toastify__slide-exit--top-right, .Toastify__slide-exit--bottom-right': {
    animationName: Toastify__slideOutRight,
  },
  '.Toastify__slide-exit--top-center': {
    animationName: Toastify__slideOutUp,
  },
  '.Toastify__slide-exit--bottom-center': {
    animationName: Toastify__slideOutDown,
  },
  '.Toastify__progress-bar': {
    animation: `${Toastify__trackProgress} linear 1`,
  },
} as const;

injectGlobal({
  ':root:not(.w-editor)': cssVars,
  ...classDeclarations,
  ...animationClasses,
});

export const ToastBodyView = emotionStyled(NoteBody)({
  maxWidth: TOAST_CONTENT_MAX_WIDTH,
  wordBreak: 'break-word',
});

export const ToastView = emotionStyled(Note)(
  {
    ...restyleNoteVars({
      iconColor: tokens.colors.actionPrimaryText,
      textColor: tokens.colors.actionPrimaryText,
      titleColor: tokens.colors.actionPrimaryText,
      closeButtonSvgColor: tokens.colors.white,
      closeButtonSvgHoverColor: tokens.colors.white,
    }),
    borderRadius: 'unset',
    color: '$actionPrimaryText' /* restyle Title color */,
    width: '100%',
  },
  {
    componentName: 'Toast',
    variants: {
      colorVariant: {
        default: {
          color: '$textPrimary',
          ...restyleNoteVars({
            tintedBackground: tokens.colors.background3,
            textColor: tokens.colors.textPrimary,
            titleColor: tokens.colors.textPrimary,
          }),
        },
        primary: {
          ...restyleNoteVars(
            {
              tintedBackground: tokens.colors.blueBackground,
              closeButtonFocusOutlineColor: tokens.colors.white,
            },
            {setAll: false}
          ),
        },
        danger: {
          ...restyleNoteVars(
            {
              tintedBackground: tokens.colors.redBackground,
              closeButtonFocusOutlineColor: tokens.colors.white,
            },
            {setAll: false}
          ),
        },
        warning: {
          ...restyleNoteVars(
            {
              iconColor: tokens.colors.black,
              tintedBackground: tokens.colors.yellowBackground,
              textColor: tokens.colors.black,
              titleColor: tokens.colors.black,
              closeButtonSvgColor: tokens.colors.black,
              closeButtonSvgHoverColor: tokens.colors.black,
              closeButtonHoverBackground: tokens.colors.blackA10,
            },
            {setAll: false}
          ),
        },
        success: {
          ...restyleNoteVars(
            {
              tintedBackground: tokens.colors.greenBackground,
              closeButtonFocusOutlineColor: tokens.colors.white,
            },
            {setAll: false}
          ),
        },
        upgrade: {
          /**
           * We can't map upgrade to success in Toast component itself.
           * The underlying Note needs the upgrade value so it can pick the correct icon.
           */
          ...restyleNoteVars(
            {
              tintedBackground: tokens.colors.greenBackground,
              closeButtonFocusOutlineColor: tokens.colors.white,
            },
            {setAll: false}
          ),
        },
      },
      hasTitle: {
        true: {},
      },
    },
    compoundVariants: [
      {
        conditions: {hasTitle: true, colorVariant: 'default'},
        css: {
          ...restyleNoteVars(
            {iconColor: tokens.colors.textPrimary},
            {setAll: false}
          ),
        },
      },

      {
        conditions: {
          colorVariant: ['primary', 'danger', 'success', 'upgrade'],
        },
        css: {
          [`&& a:not(${Button})`]: {
            color: '$actionPrimaryText',
            textDecoration: 'underline',
          },
          [`&& a${selectors.hovered}:not(${Button})`]: {
            color: coreColorTokens.d2.gray[100],
          },
        },
      },
      {
        conditions: {colorVariant: ['warning']},
        css: {
          [`&& a:not(${Button})`]: {
            color: '$black',
            textDecoration: 'underline',
          },
          [`&& a${selectors.hovered}:not(${Button})`]: {
            color: tokens.colors.blackA75,
          },

          /* Selector manually added here until utils/selectors is changed to not include ampersand */
          [`& ${CloseButton} svg`]: {
            color: '$black',
          },
        },
      },
    ],
  }
);
